import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { catchError, concatMap, finalize, map, mergeMap, startWith } from 'rxjs/operators';

import * as appWizardActions from '../../../app-wizard/store/actions/app-wizard.actions';

import { SiteState } from '../../../shared/models/siteState.model';
import { AuthorizationService } from '../../../shared/services/authorization.service';

@Injectable()
export class AppWizardEffects {
  getApplicationGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.getApplicationGroups),
      mergeMap(() =>
        this.authorizationService.getApplicationGroups().pipe(
          map((applicationGroups) =>
            appWizardActions.getApplicationGroupsSuccess({
              applicationGroups,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getApplications$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.getApplications),
      mergeMap(() =>
        this.authorizationService.getApplications().pipe(
          map((applications) =>
            appWizardActions.getApplicationsSuccess({
              applications,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getApplicationRoles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.getApplicationRoles),
      mergeMap(() =>
        this.authorizationService.getRoles().pipe(
          map((roles) =>
            appWizardActions.getApplicationRolesSuccess({
              roles,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  createNewApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.createNewApplication),
      mergeMap(({ newApplication }) =>
        this.authorizationService.createNewApplication(newApplication).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: 'app creation succeeded',
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  createNewApplicationGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.createNewApplicationGroup),
      mergeMap(({ newApplicationGroup }) =>
        this.authorizationService.createNewApplicationGroup(newApplicationGroup).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: 'app group creation succeeded',
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteApplicationGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.deleteApplicationGroup),
      mergeMap(({ id }) =>
        this.authorizationService.deleteApplicationGroup(id).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: 'app group deletion succeeded',
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteApplication$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(appWizardActions.deleteApplication),
      mergeMap(({ key }) =>
        this.authorizationService.deleteApplication(key).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: 'app deletion succeeded',
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly authorizationService: AuthorizationService,
    private store: Store<SiteState>,
  ) {}
}
